import React from 'react';

import { useIntl } from 'react-intl';

import { INPUT_METHOD } from '@atlaskit/editor-common/analytics';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { moveRowUp, tooltip } from '@atlaskit/editor-common/keymaps';
import { tableMessages as messages } from '@atlaskit/editor-common/messages';
import { getSelectionRect } from '@atlaskit/editor-tables/utils';
import { InformationCircleIcon } from '@atlaskit/editor-toolbar/information-circle-icon';
import { TableRowMoveUpIcon } from '@atlaskit/editor-toolbar/row-move-up-icon';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import { ToolbarKeyboardShortcutHint } from '@atlaskit/editor-toolbar/toolbar-keyboard-shortcut-hint';
import { Text } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { closeActiveTableMenu } from '../../../../pm-plugins/commands';
import { moveSourceWithAnalytics } from '../../../../pm-plugins/drag-and-drop/commands-with-analytics';
import { getPluginState } from '../../../../pm-plugins/plugin-factory';
import { getSelectedRowIndexes } from '../../../../pm-plugins/utils/selection';
import type { TableSharedStateInternal } from '../../../../types';
import { TABLE_ROW } from '../../shared/consts';
import { useTableMenuContext } from '../../shared/TableMenuContext';
import type { TableMenuComponentsParams } from '../../shared/types';

const shouldShowMoveRowUp = (isFirstRow?: boolean): boolean => !isFirstRow;

export const MoveRowUpItem = (props: TableMenuComponentsParams): React.JSX.Element | null => {
	const { api } = props;
	const tableMenuContext = useTableMenuContext();
	const { editorView } = tableMenuContext ?? {};
	const { tableNode } = useSharedPluginStateWithSelector(api ?? undefined, ['table'], (states) => ({
		tableNode: (states.tableState as TableSharedStateInternal | undefined)?.tableNode,
	}));
	const selectedRowCount = tableMenuContext?.selectedRowCount ?? 1;
	const { formatMessage } = useIntl();

	const handleClick = () => {
		if (!editorView) {
			return;
		}

		const selectionRect = getSelectionRect(editorView.state.selection);
		if (!selectionRect) {
			return;
		}
		moveSourceWithAnalytics(
			api?.analytics?.actions,
			api?.accessibilityUtils?.actions.ariaNotify,
			getPluginState(editorView.state).getIntl,
		)(
			INPUT_METHOD.TABLE_CONTEXT_MENU,
			TABLE_ROW,
			getSelectedRowIndexes(selectionRect),
			selectionRect.top - 1,
		)(editorView.state, editorView.dispatch);
		api?.core.actions.execute(closeActiveTableMenu(api));
		api?.core.actions.focus();
	};

	if (!tableNode || !shouldShowMoveRowUp(tableMenuContext?.isFirstRow)) {
		return null;
	}

	const label = formatMessage(messages.moveRowUp, { 0: selectedRowCount });
	const canMove = Boolean(tableMenuContext?.canMoveRowUp);
	const isMoveDisabled =
		expValEquals('platform_editor_table_menu_updates_patch_2', 'isEnabled', true) && !canMove;

	if (isMoveDisabled) {
		return (
			<ToolbarDropdownItem
				elemBefore={
					<TableRowMoveUpIcon color={token('color.text.disabled')} label="" size="small" />
				}
				elemAfter={
					<Tooltip content={formatMessage(messages.canNotMoveRowUpWithMergedCells)} position="top">
						{(tooltipProps) => (
							// Ignored via go/ees005
							// eslint-disable-next-line react/jsx-props-no-spreading
							<span {...tooltipProps}>
								<InformationCircleIcon label="" size="small" color={token('color.icon')} />
							</span>
						)}
					</Tooltip>
				}
			>
				<Text color="color.text.disabled">{label}</Text>
			</ToolbarDropdownItem>
		);
	}

	return (
		<ToolbarDropdownItem
			onClick={handleClick}
			elemBefore={<TableRowMoveUpIcon color="currentColor" label="" size="small" />}
			elemAfter={<ToolbarKeyboardShortcutHint shortcut={tooltip(moveRowUp) ?? ''} />}
		>
			{label}
		</ToolbarDropdownItem>
	);
};
